/**
 * Filter validation and suggestion utilities
 */
import type { ExtendedUnitFilters } from './units';
import { formatFloorDisplay } from './utils';

export interface FilterSuggestion {
  message: string;
  suggestedFilters: ExtendedUnitFilters;
  reason: string;
}

/**
 * Validate filter combinations and provide suggestions
 * Returns null if combination is valid, or a suggestion if problematic
 */
export function validateFilterCombination(
  filters: ExtendedUnitFilters
): FilterSuggestion | null {
  // Known invalid combinations based on actual unit data
  const invalidCombinations = [
    {
      check: (f: ExtendedUnitFilters) => f.category === '2bed' && f.viewType === 'canal',
      message: 'No 2-bedroom units with Canal views available',
      suggestion: (f: ExtendedUnitFilters) => ({ ...f, viewType: 'lagoon' as const }),
      reason: 'All 2-bedroom units have lagoon views',
    },
    {
      check: (f: ExtendedUnitFilters) => f.floor === 'fourth' && f.viewType === 'canal',
      message: 'No Penthouse units with Canal views available',
      suggestion: (f: ExtendedUnitFilters) => ({ ...f, viewType: 'lagoon' as const }),
      reason: 'All penthouse units have lagoon views',
    },
    {
      check: (f: ExtendedUnitFilters) => f.category === '2bed' && f.floor === 'fourth',
      message: 'No 2-bedroom Penthouse units available',
      suggestion: (f: ExtendedUnitFilters) => ({ ...f, floor: undefined }),
      reason: 'Try removing the floor filter to see 2-bedroom options',
    },
  ];

  for (const combo of invalidCombinations) {
    if (combo.check(filters)) {
      return {
        message: combo.message,
        suggestedFilters: combo.suggestion(filters),
        reason: combo.reason,
      };
    }
  }

  return null;
}

/**
 * Get filter display labels for user-friendly messages
 */
export function getFilterLabel(key: string, value: string | number | boolean | null | undefined): string {
  if (value === null || value === undefined) {
    return "N/A";
  }
  
  if (typeof value === 'boolean') {
    return value ? "Yes" : "No";
  }

  const labels: Record<string, Record<string, string>> = {
    category: {
      '1bed': '1 Bedroom Condo',
      '2bed': '2 Bedroom Condo',
      townhouse: '3 Bedroom Townhouse',
    },
    viewType: {
      lagoon: 'Lagoon View',
      canal: 'Canal View',
    },
    floor: {
      GF: 'Ground Floor',
      second: '2nd Floor',
      third: '3rd Floor',
      fourth: '4th Floor',
    },
    block: {
      A: 'Canal View',
      B: 'Lagoon View',
    },
    availability: {
      available: 'Available',
      reserved: 'Reserved',
      sold: 'Sold',
    },
  };

  const stringValue = String(value);
  return labels[key]?.[stringValue] || stringValue;
}

/**
 * Get active filter labels for display
 */
export function getActiveFilterLabels(filters: ExtendedUnitFilters): string[] {
  const labels: string[] = [];

  if (filters.category) labels.push(getFilterLabel('category', filters.category));
  if (filters.viewType) labels.push(getFilterLabel('viewType', filters.viewType));
  if (filters.floor) labels.push(getFilterLabel('floor', filters.floor));
  if (filters.block) labels.push(getFilterLabel('block', filters.block));
  if (filters.bedrooms !== undefined) labels.push(`${filters.bedrooms} Bedrooms`);
  if (filters.bathrooms !== undefined) labels.push(`${filters.bathrooms} Bathrooms`);
  if (filters.availability) labels.push(getFilterLabel('availability', filters.availability));
  if (filters.minPrice !== undefined) labels.push(`Min: $${filters.minPrice.toLocaleString()}`);
  if (filters.maxPrice !== undefined) labels.push(`Max: $${filters.maxPrice.toLocaleString()}`);
  if (filters.minArea !== undefined) labels.push(`Min: ${filters.minArea} sq ft`);
  if (filters.maxArea !== undefined) labels.push(`Max: ${filters.maxArea} sq ft`);

  return labels;
}

/**
 * Get filter state key for localStorage
 */
export function getFilterStorageKey(browseMode: string): string {
  return `residences_filters_${browseMode}`;
}

/**
 * Save filters to localStorage
 */
export function saveFiltersToStorage(browseMode: string, filters: ExtendedUnitFilters): void {
  try {
    const key = getFilterStorageKey(browseMode);
    localStorage.setItem(key, JSON.stringify(filters));
  } catch (error) {
    console.error('Failed to save filters to localStorage:', error);
  }
}

/**
 * Load filters from localStorage
 */
export function loadFiltersFromStorage(browseMode: string): ExtendedUnitFilters | null {
  try {
    const key = getFilterStorageKey(browseMode);
    const stored = localStorage.getItem(key);
    if (stored) {
      return JSON.parse(stored) as ExtendedUnitFilters;
    }
  } catch (error) {
    console.error('Failed to load filters from localStorage:', error);
  }
  return null;
}

/**
 * Clear filters from localStorage
 */
export function clearFiltersFromStorage(browseMode: string): void {
  try {
    const key = getFilterStorageKey(browseMode);
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Failed to clear filters from localStorage:', error);
  }
}
